import { cn } from '@/lib/utils';
import { PANEL } from '@/lib/surface';
import React, { useState, useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format } from 'date-fns';
import { MapComponent } from '@/components/MapComponent';
import { motion } from 'framer-motion';

// UI Components
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Progress } from '@/components/ui/progress';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// Icons
import {
  Search,
  Ship,
  Plane,
  Truck,
  Package,
  AlertTriangle,
  CheckCircle,
  MapPin,
  Clock,
  FileText,
} from 'lucide-react';

// API
import {
  trackingApi,
  ShipmentTracking,
  TrackingEvent,
  getLocationLat,
  getLocationLng,
} from '@/api/services/tracking.api';
import { useAuth } from '@/context/AuthContext';
import { useShipmentTracking } from '@/hooks/useShipmentTracking';

// Track & Trace Components
import { KPIDashboard } from '@/components/tracking/KPIDashboard';
import { AdvancedFilters, FilterState } from '@/components/tracking/AdvancedFilters';
import { DemurragePanel } from '@/components/tracking/DemurragePanel';
import { DocumentsPanel } from '@/components/tracking/DocumentsPanel';
import { PageHeader, ModeBadge } from '@/components/common';
import { EmptyValue } from '@/components/ui/table';

// Design System
import {
  getShipmentStatusStyle,
  getTimelineEventColor,
  getTimelineEventBorderStyle,
  getWebSocketStatusStyle,
  getWebSocketDotColor,
  getModeIconStyle,
  getAlertStyle,
  getAlertIconColor,
  CARD_STYLES,
} from '@/lib/tracking-design-system';
import { stagger, fadeUp } from '@/lib/animations';

// ============================================
// Helper Functions
// ============================================

const getModeIcon = (mode: string) => {
  switch (mode) {
    case 'OCEAN':
      return Ship;
    case 'AIR':
      return Plane;
    case 'ROAD_FTL':
    case 'ROAD_LTL':
      return Truck;
    default:
      return Package;
  }
};

const calculateProgress = (shipment: ShipmentTracking): number => {
  if (!shipment.position || !shipment.origin || !shipment.destination) return 0;

  const originLat = getLocationLat(shipment.origin);
  const originLon = getLocationLng(shipment.origin);
  const destLat = getLocationLat(shipment.destination);
  const destLon = getLocationLng(shipment.destination);
  const currentLat = shipment.position.latitude;
  const currentLon = shipment.position.longitude;

  const totalDistLat = destLat - originLat;
  const totalDistLon = destLon - originLon;
  const currentDistLat = currentLat - originLat;
  const currentDistLon = currentLon - originLon;

  const progress = Math.sqrt(
    (currentDistLat * currentDistLat + currentDistLon * currentDistLon) /
      (totalDistLat * totalDistLat + totalDistLon * totalDistLon)
  );

  return Math.min(Math.max(progress * 100, 0), 100);
};

const formatRelativeTime = (date: string): string => {
  const now = new Date();
  const target = new Date(date);
  const diffMs = target.getTime() - now.getTime();
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`;
  if (diffHours > 0) return `${diffHours}h`;
  if (diffMs < 0) return 'Overdue';
  return 'Less than 1h';
};

// ============================================
// Components
// ============================================

function SearchBar({
  onSearch,
  connectionStatus,
}: {
  onSearch: (query: string) => void;
  connectionStatus?: string;
}) {
  const [query, setQuery] = useState('');

  return (
    <div className="p-4 border-b">
      <div className="flex items-center gap-2 mb-2">
        <div className="flex-1 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Search by tracking #, BOL, container, AWB..."
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              onSearch(e.target.value);
            }}
            className="pl-9"
          />
        </div>

        {/* WebSocket Connection Status (Phase 2) */}
        {connectionStatus && (
          <Badge
            variant="outline"
            className={getWebSocketStatusStyle(
              connectionStatus as 'connected' | 'connecting' | 'disconnected' | 'offline'
            )}
          >
            <div className="flex items-center gap-1">
              <div
                className={`w-2 h-2 rounded-full ${getWebSocketDotColor(
                  connectionStatus as 'connected' | 'connecting' | 'disconnected' | 'offline'
                )} ${connectionStatus === 'connected' ? 'animate-pulse' : ''}`}
              />
              {connectionStatus === 'connected'
                ? 'Live'
                : connectionStatus === 'connecting'
                  ? 'Connecting'
                  : 'Offline'}
            </div>
          </Badge>
        )}
      </div>
    </div>
  );
}

function ShipmentCard({
  shipment,
  isSelected,
  onClick,
}: {
  shipment: ShipmentTracking;
  isSelected: boolean;
  onClick: () => void;
}) {
  const ModeIcon = getModeIcon(shipment.transportMode || 'ROAD_FTL');
  const progress = calculateProgress(shipment);

  return (
    <div
      className={`p-4 border-b ${CARD_STYLES.interactive} ${
        isSelected ? CARD_STYLES.selected : ''
      }`}
      onClick={onClick}
      data-testid="shipment-card"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div
            className={`p-2 rounded-lg ${getModeIconStyle(shipment.transportMode || 'ROAD_FTL')}`}
          >
            <ModeIcon className="w-5 h-5" />
          </div>
          <div>
            <div className="font-medium">{shipment.trackingNumber || shipment.shipmentNumber}</div>
            <div className="text-xs leading-4 text-muted-foreground">
              {shipment.origin.address?.city} → {shipment.destination.address?.city}
            </div>
          </div>
        </div>

        <Badge variant="outline" className={getShipmentStatusStyle(shipment.status)}>
          {shipment.status.replace('_', ' ')}
        </Badge>
      </div>

      {/* Progress Bar */}
      <div className="mb-2">
        <div className="flex justify-between text-xs mb-1">
          <span className="text-muted-foreground">Progress</span>
          <span className="font-medium">{Math.round(progress)}%</span>
        </div>
        <Progress value={progress} className="h-1.5" />
      </div>

      {/* ETA */}
      {shipment.estimatedArrival && (
        <div className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />
          ETA: {formatRelativeTime(shipment.estimatedArrival)}
        </div>
      )}
    </div>
  );
}

const MapView = React.memo(({ shipment }: { shipment: ShipmentTracking | null }) => {
  if (!shipment) {
    return (
      <div className="h-full flex items-center justify-center bg-muted/10">
        <div className="text-center">
          <Package className="w-12 h-12 mx-auto mb-2 text-muted-foreground" />
          <p className="text-muted-foreground">Select a shipment to view tracking</p>
        </div>
      </div>
    );
  }

  const originCoords: [number, number] = [
    getLocationLat(shipment.origin),
    getLocationLng(shipment.origin),
  ];

  const destCoords: [number, number] = [
    getLocationLat(shipment.destination),
    getLocationLng(shipment.destination),
  ];

  const currentCoords: [number, number] | null = shipment.position
    ? [shipment.position.latitude, shipment.position.longitude]
    : null;

  // Calculate map center
  const center: [number, number] = currentCoords || [
    (originCoords[0] + destCoords[0]) / 2,
    (originCoords[1] + destCoords[1]) / 2,
  ];

  // Route path
  const routePath: [number, number][] = currentCoords
    ? [originCoords, currentCoords, destCoords]
    : [originCoords, destCoords];

  // Prepare markers for MapComponent
  const markers = [
    {
      position: originCoords,
      color: 'green' as const,
      popup: `<strong>Origin</strong><br/>${shipment.origin.name}<br/>${shipment.origin.address?.city}, ${shipment.origin.address?.country}`,
    },
    {
      position: destCoords,
      color: 'red' as const,
      popup: `<strong>Destination</strong><br/>${shipment.destination.name}<br/>${shipment.destination.address?.city}, ${shipment.destination.address?.country}`,
    },
  ];

  if (currentCoords) {
    markers.splice(1, 0, {
      position: currentCoords,
      color: 'green' as const,
      popup: `<strong>Current Location</strong><br/>${shipment.currentLocation?.city || 'In Transit'}<br/>Last update: ${shipment.lastPositionUpdate ? format(new Date(shipment.lastPositionUpdate), 'MMM d, HH:mm') : 'N/A'}`,
    });
  }

  return (
    <MapComponent
      center={center}
      zoom={5}
      markers={markers}
      routePath={routePath}
      className="h-full w-full"
    />
  );
});

function TimelineView({ events }: { events: TrackingEvent[] }) {
  if (!events || events.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <p className="text-muted-foreground">No tracking events available</p>
      </div>
    );
  }

  // Group events by date
  const groupedEvents: Record<string, TrackingEvent[]> = {};
  events.forEach((event) => {
    const date = format(new Date(event.timestamp), 'yyyy-MM-dd');
    if (!groupedEvents[date]) groupedEvents[date] = [];
    groupedEvents[date].push(event);
  });

  const getEventIcon = (type: string) => {
    switch (type) {
      case 'PICKED_UP':
      case 'LOADED':
        return Package;
      case 'IN_TRANSIT':
        return Truck;
      case 'CUSTOMS_CLEARED':
      case 'SECURITY_CLEARED':
        return FileText;
      case 'DELIVERED':
        return CheckCircle;
      case 'EXCEPTION':
        return AlertTriangle;
      default:
        return MapPin;
    }
  };

  return (
    <ScrollArea className="h-full p-4">
      {Object.entries(groupedEvents).map(([date, dayEvents]) => (
        <div key={date} className="mb-6">
          <div className="text-sm font-medium text-muted-foreground mb-3">
            {format(new Date(date), 'MMMM d, yyyy')}
          </div>

          <div className="relative pl-8 space-y-4">
            {/* Vertical timeline line */}
            <div className="absolute left-2 top-0 bottom-0 w-0.5 bg-border" />

            {dayEvents.map((event) => {
              const Icon = getEventIcon(event.eventType);
              const eventColor = getTimelineEventColor(event.eventType);
              const eventBorderStyle = getTimelineEventBorderStyle(event.eventType);

              return (
                <div key={event.id} className="relative">
                  {/* Timeline dot/icon */}
                  <div className={`absolute -left-6 p-1.5 rounded-full ${eventColor}`}>
                    <Icon className="w-3 h-3 text-white" />
                  </div>

                  {/* Event content */}
                  <div className={`border rounded-lg p-3 ${eventBorderStyle}`}>
                    <div className="flex justify-between items-start">
                      <div>
                        <div className="font-medium">{event.eventType.replace(/_/g, ' ')}</div>
                        {event.location && (
                          <div className="text-sm text-muted-foreground mt-1">
                            {event.location.city}, {event.location.country}
                          </div>
                        )}
                        {event.description && (
                          <div className="text-sm mt-2">{event.description}</div>
                        )}
                      </div>

                      <div className="text-xs text-muted-foreground whitespace-nowrap">
                        {format(new Date(event.timestamp), 'HH:mm')}
                      </div>
                    </div>

                    {/* Metadata */}
                    {event.metadata && Object.keys(event.metadata).length > 0 && (
                      <div className="mt-2 text-xs space-y-0.5">
                        {Object.entries(event.metadata).map(([key, value]) => (
                          <div key={key} className="text-muted-foreground">
                            <span className="font-medium">{key}:</span> {String(value)}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </ScrollArea>
  );
}

function TemperatureChart({ shipmentId }: { shipmentId: string }) {
  const [hours, setHours] = useState(24);

  const { data: logs = [] } = useQuery({
    queryKey: ['temperature', shipmentId, hours],
    queryFn: () => trackingApi.getTemperature(shipmentId, hours),
    enabled: !!shipmentId,
  });

  const minTemp = 2; // °C
  const maxTemp = 8; // °C

  const chartData = logs.map((log) => ({
    timestamp: new Date(log.timestamp).getTime(),
    temperature: log.temperature,
    humidity: log.humidity,
  }));

  const alertCount = logs.filter((log) => log.isAlert).length;

  return (
    <div className="p-4">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-base font-semibold">Temperature Log</h3>
        <Select value={hours.toString()} onValueChange={(v) => setHours(Number(v))}>
          <SelectTrigger className="w-32">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24">Last 24h</SelectItem>
            <SelectItem value="72">Last 3 days</SelectItem>
            <SelectItem value="168">Last 7 days</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {chartData.length > 0 ? (
        <>
          <ResponsiveContainer width="100%" height={250}>
            <LineChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="timestamp" tickFormatter={(t) => format(new Date(t), 'HH:mm')} />
              <YAxis label={{ value: 'Temperature (°C)', angle: -90, position: 'insideLeft' }} />
              <Tooltip
                labelFormatter={(t) => format(new Date(t), 'MMM d, HH:mm')}
                formatter={(value: number) => [`${value}°C`, 'Temperature']}
              />

              {/* Temperature line */}
              <Line
                type="monotone"
                dataKey="temperature"
                stroke="#3b82f6"
                strokeWidth={2}
                dot={{ r: 3 }}
              />

              {/* Threshold lines */}
              <ReferenceLine y={minTemp} stroke="#ef4444" strokeDasharray="3 3" label="Min" />
              <ReferenceLine y={maxTemp} stroke="#ef4444" strokeDasharray="3 3" label="Max" />
            </LineChart>
          </ResponsiveContainer>

          {alertCount > 0 && (
            <div
              className={`mt-4 p-3 border rounded-lg flex items-start gap-2 ${getAlertStyle('warning')}`}
            >
              <AlertTriangle className={`w-4 h-4 mt-0.5 ${getAlertIconColor('warning')}`} />
              <div className="text-sm">
                {alertCount} temperature excursion{alertCount > 1 ? 's' : ''} detected in the
                selected period
              </div>
            </div>
          )}
        </>
      ) : (
        <div className="h-64 flex items-center justify-center text-muted-foreground">
          No temperature data available
        </div>
      )}
    </div>
  );
}

// ============================================
// Main Component
// ============================================

export default function TrackTrace() {
  const { user } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter] = useState<string | null>(null);
  const [selectedShipment, setSelectedShipment] = useState<ShipmentTracking | null>(null);
  const [activeTab, setActiveTab] = useState<string>('timeline');

  // Advanced filters state
  const [advancedFilters, setAdvancedFilters] = useState<FilterState>({
    exceptionsOnly: false,
  });

  // Phase 2: WebSocket real-time tracking
  const { connectionStatus, latestPosition, latestEvent, latestETA } = useShipmentTracking(
    selectedShipment?.id || null
  );

  // Fetch active shipments
  const {
    data: shipmentsData,
    isLoading,
    refetch: refetchShipments,
  } = useQuery({
    queryKey: ['tracking-shipments', user?.companyId, searchQuery, statusFilter],
    queryFn: () =>
      trackingApi.getActiveShipments({
        companyId: user?.companyId,
        search: searchQuery || undefined,
        status: statusFilter || undefined,
      }),
    enabled: !!user?.companyId,
  });

  // Fetch selected shipment details
  const { data: shipmentDetails, refetch: refetchShipment } = useQuery({
    queryKey: ['tracking-shipment', selectedShipment?.id],
    queryFn: () => trackingApi.getShipmentTracking(selectedShipment!.id),
    enabled: !!selectedShipment,
  });

  const shipments = useMemo(() => shipmentsData?.data || [], [shipmentsData]);
  const timeline = shipmentDetails?.trackingEvents || [];

  // Debug: Log query state
  useEffect(() => {
    console.log('[TrackTrace Debug]', {
      hasUser: !!user,
      companyId: user?.companyId,
      queryEnabled: !!user?.companyId,
      isLoading,
      shipmentsCount: shipments.length,
      shipmentsData,
    });
  }, [user, isLoading, shipments, shipmentsData]);

  // Auto-select first shipment
  useEffect(() => {
    if (shipments.length > 0 && !selectedShipment) {
      setSelectedShipment(shipments[0]);
    }
  }, [shipments, selectedShipment]);

  // Phase 2: Update selected shipment when position update received
  useEffect(() => {
    if (latestPosition && selectedShipment) {
      console.log('Position update received, refreshing shipment data');
      refetchShipment();
    }
  }, [latestPosition, selectedShipment, refetchShipment]);

  // Phase 2: Refresh shipment list when new event received
  useEffect(() => {
    if (latestEvent) {
      console.log('New event received, refreshing data');
      refetchShipment();
      refetchShipments();
    }
  }, [latestEvent, refetchShipment, refetchShipments]);

  // Phase 2: Refresh when ETA updated
  useEffect(() => {
    if (latestETA) {
      console.log('ETA updated, refreshing data');
      refetchShipment();
    }
  }, [latestETA, refetchShipment]);

  return (
    <motion.div
      className="flex flex-col h-[calc(100vh-4rem)]"
      variants={stagger}
      initial="hidden"
      animate="show"
    >
      {/* Header */}
      <motion.div variants={fadeUp} className="border-b bg-background p-6">
        <PageHeader
          title="Track & Trace"
          description="Shipments in transit, with the last reported position and milestone for each."
        />
      </motion.div>

      {/* KPI Dashboard */}
      <motion.div variants={fadeUp} className="border-b bg-background p-4">
        <KPIDashboard />
      </motion.div>

      {/* Two-Panel Layout */}
      <motion.div variants={fadeUp} className="flex flex-1 overflow-hidden">
        {/* Left Panel: Shipment List */}
        <div className="w-2/5 border-r flex flex-col bg-background">
          <SearchBar onSearch={setSearchQuery} connectionStatus={connectionStatus} />

          {/* Advanced Filters - Replace QuickFilters */}
          <div className="p-4 border-b">
            <AdvancedFilters
              filters={advancedFilters}
              onFiltersChange={setAdvancedFilters}
              carriers={[]} // TODO: Add carriers from API
            />
          </div>

          <ScrollArea className="flex-1">
            {isLoading ? (
              <div className="p-8 text-center text-muted-foreground">Loading shipments...</div>
            ) : shipments.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground">No shipments found</div>
            ) : (
              shipments.map((shipment) => (
                <ShipmentCard
                  key={shipment.id}
                  shipment={shipment}
                  isSelected={selectedShipment?.id === shipment.id}
                  onClick={() => setSelectedShipment(shipment)}
                />
              ))
            )}
          </ScrollArea>
        </div>

        {/* Right Panel: Map + Details */}
        <div className="w-3/5 flex flex-col">
          {/* Map Section (60% height) */}
          <div className={cn(PANEL, 'h-3/5 overflow-hidden')}>
            <MapView shipment={shipmentDetails || selectedShipment} />
          </div>

          {/* Tabbed Details (40% height) */}
          <div className="h-2/5 border-t bg-background">
            {selectedShipment ? (
              <Tabs value={activeTab} onValueChange={setActiveTab} className="h-full flex flex-col">
                <TabsList className="w-full">
                  <TabsTrigger value="timeline">Timeline</TabsTrigger>
                  <TabsTrigger value="details">Details</TabsTrigger>
                  {shipmentDetails?.temperatureLogs &&
                    shipmentDetails.temperatureLogs.length > 0 && (
                      <TabsTrigger value="temperature">Temperature</TabsTrigger>
                    )}
                  {(shipmentDetails?.freeTimeExpiry ||
                    (shipmentDetails?.totalDemurrageCharges &&
                      shipmentDetails.totalDemurrageCharges > 0) ||
                    shipmentDetails?.demurrageStatus) && (
                    <TabsTrigger value="demurrage">
                      Demurrage
                      {shipmentDetails?.demurrageStatus === 'FREE_TIME_EXPIRING' && (
                        <Badge variant="destructive" className="ml-2">
                          Expiring
                        </Badge>
                      )}
                      {shipmentDetails?.demurrageStatus === 'DEMURRAGE_ACCRUING' && (
                        <Badge variant="destructive" className="ml-2">
                          Accruing
                        </Badge>
                      )}
                    </TabsTrigger>
                  )}
                  <TabsTrigger value="documents">Documents</TabsTrigger>
                </TabsList>

                <div className="flex-1 overflow-hidden">
                  <TabsContent value="timeline" className="h-full mt-0">
                    <TimelineView events={timeline} />
                  </TabsContent>

                  <TabsContent value="details" className="h-full mt-0 p-4">
                    <ScrollArea className="h-full">
                      <div className="space-y-4">
                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Tracking Number</div>
                          <div className="font-medium">
                            {selectedShipment.trackingNumber || 'N/A'}
                          </div>
                        </div>

                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Carrier</div>
                          <div className="font-medium">{selectedShipment.carrier || 'N/A'}</div>
                        </div>

                        <div>
                          <div className="text-sm text-muted-foreground mb-1">Transport Mode</div>
                          {selectedShipment.transportMode ? (
                            <ModeBadge mode={selectedShipment.transportMode} />
                          ) : (
                            <EmptyValue />
                          )}
                        </div>

                        <div className="grid grid-cols-2 gap-4">
                          <div>
                            <div className="text-sm text-muted-foreground mb-1">Weight</div>
                            <div className="font-medium">
                              {selectedShipment.totalWeight
                                ? `${selectedShipment.totalWeight} kg`
                                : 'N/A'}
                            </div>
                          </div>
                          <div>
                            <div className="text-sm text-muted-foreground mb-1">Volume</div>
                            <div className="font-medium">
                              {selectedShipment.totalVolume
                                ? `${selectedShipment.totalVolume} m³`
                                : 'N/A'}
                            </div>
                          </div>
                        </div>

                        <div>
                          <div className="text-sm text-muted-foreground mb-1">
                            Estimated Arrival
                          </div>
                          <div className="font-medium">
                            {selectedShipment.estimatedArrival
                              ? format(
                                  new Date(selectedShipment.estimatedArrival),
                                  'MMM d, yyyy HH:mm'
                                )
                              : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </ScrollArea>
                  </TabsContent>

                  <TabsContent value="temperature" className="h-full mt-0">
                    <TemperatureChart shipmentId={selectedShipment.id} />
                  </TabsContent>

                  <TabsContent value="demurrage" className="h-full mt-0 p-4">
                    <ScrollArea className="h-full">
                      <DemurragePanel shipmentId={selectedShipment.id} />
                    </ScrollArea>
                  </TabsContent>

                  <TabsContent value="documents" className="h-full mt-0 p-4">
                    <ScrollArea className="h-full">
                      <DocumentsPanel shipmentId={selectedShipment.id} />
                    </ScrollArea>
                  </TabsContent>
                </div>
              </Tabs>
            ) : (
              <div className="h-full flex items-center justify-center">
                <p className="text-muted-foreground">Select a shipment to view details</p>
              </div>
            )}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}
